import React, { useState } from "react";
import { Stack, Text, TextField, PrimaryButton, DefaultButton } from "@fluentui/react";
import { AccountRegService } from "../../services";

interface Props {
    accountRegId: number;
    onCreated: (customerRef: string) => void;
    onBack: () => void;
    companyName: string;
}

export const StepCreateAccountRef: React.FC<Props> = ({ companyName, accountRegId, onCreated, onBack }) => {
    const [customerRef, setCustomerRef] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const validateRef = (ref: string) => /^[a-z0-9\-]+$/.test(ref);

    const generateCustomerRef = (name: string) => {
        if (!name) return "";

        const stopWords = ["ltd", "limited", "corp", "corporation", "inc", "llc", "plc"];

        return name
            .toLowerCase()
            .split(/\s+/)
            .filter(word => !stopWords.includes(word))
            .join("")
            .replace(/[^a-z0-9-]/g, "");
    };
    const suggestedRef = generateCustomerRef(companyName);

    const handleCreate = async () => {
        const trimmedRef = customerRef.trim();
        
        if (!trimmedRef) {
            setError("Account ID is required");
            return;
        }

        if (trimmedRef.startsWith("_")) {
            setError("Account ID cannot start with underscore");
            return;
        }

        if (!validateRef(trimmedRef)) {
            setError("Only lowercase letters, numbers and hyphen allowed");
            return;
        }

        setLoading(true);
        setError("");

        try {
            onCreated(customerRef);
        } catch (err) {
            setError("Failed to save account ID");
        }

        setLoading(false);
    };

    return (
        <Stack tokens={{ childrenGap: 20 }} styles={{ root: { maxWidth: 400 } }}>
            <Text variant="xLarge">Create Your Account ID</Text>
            <Text>Enter a unique account ID. This will be used in your account URL.</Text>

            <TextField
                label="Account ID"
                value={customerRef}
                onChange={(_, val) => setCustomerRef(val || "")}
                placeholder={`example: ${suggestedRef}`}
                errorMessage={error}
            />

            <Stack horizontal tokens={{ childrenGap: 10 }}>
                <PrimaryButton text="Create" onClick={handleCreate} disabled={loading} />
            </Stack>
        </Stack>
    );
};